import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { gql, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { useAuthStore } from "../../store/useAuthStore";
import { getGraphQLErrorMsg, saveAfterLoginPath } from "../../util/util";
import { loginUrl, productUrl } from "../../util/url";
import { logAddToWishlist } from "../../util/analytics";
import ThrottledButton from "../debouncedButton/ThrottledButton";

import HeartFilledIcon from "./assets/heart_filled.svg";
import HeartOutlineIcon from "./assets/heart_outline.svg";

import "./LikeButton.css";

const POST_LIKE_PRODUCT_V2 = gql`
  mutation likeProductV2($productID: Int!, $keywordId: Int) {
    likeProductV2(product_id: $productID, keyword_id: $keywordId) {
      is_enable_app_review_popup
      like_count
    }
  }
`;

const POST_UNLIKE_PRODUCT = gql`
  mutation unLikeProduct($productID: Int!) {
    unlikeProduct(id: $productID)
  }
`;

/**
 * 상품 찜 버튼
 * @param product
 * @returns {JSX.Element}
 * @constructor
 */
export default function LikeButton({ product }) {
    const navigate = useNavigate();
    const [isAnimating, setIsAnimating] = useState(false);
    const initialIsLiked = Boolean(product.is_liked);
    const initialLikeCount = Number(product.like_count || 0);
    const productId = Number(product.id);

    const [isLiked, setIsLiked] = useState(initialIsLiked);
    const [likeCount, setLikeCount] = useState(initialLikeCount);
    const pendingRef = useRef(false);
    const isLikedRef = useRef(initialIsLiked);
    const likeCountRef = useRef(initialLikeCount);

    useEffect(() => {
        if (pendingRef.current) return;
        const newIsLiked = Boolean(product.is_liked);
        const newLikeCount = Number(product.like_count || 0);
        setIsLiked(newIsLiked);
        setLikeCount(newLikeCount);
        isLikedRef.current = newIsLiked;
        likeCountRef.current = newLikeCount;
    }, [product.is_liked, product.like_count]);

    const [likeProduct] = useMutation(POST_LIKE_PRODUCT_V2);
    const [unLikeProduct] = useMutation(POST_UNLIKE_PRODUCT);

    const handleAnimationEnd = useCallback(() => {
        setIsAnimating(false);
    }, []);

    const handleLikePress = useCallback(async () => {
        const { isLogin } = useAuthStore.getState();
        if (!isLogin) {
            saveAfterLoginPath(productUrl(product));
            navigate(loginUrl());
            return;
        }

        if (pendingRef.current) return;
        pendingRef.current = true;

        const prevIsLiked = isLikedRef.current;
        const prevLikeCount = likeCountRef.current;
        const nextIsLiked = !prevIsLiked;
        const nextLikeCount = Math.max(0, prevLikeCount + (prevIsLiked ? -1 : 1));

        // 즉시 UI 반영
        isLikedRef.current = nextIsLiked;
        likeCountRef.current = nextLikeCount;
        setIsLiked(nextIsLiked);
        setLikeCount(nextLikeCount);
        setIsAnimating(true);

        try {
            if (prevIsLiked) {
                await unLikeProduct({
                    variables: { productID: productId },
                });
            } else {
                await likeProduct({
                    variables: { productID: productId },
                });
                logAddToWishlist(product);
                toast("마이 > 'Wishlist'에 추가되었습니다.");
            }
        } catch (error) {
            isLikedRef.current = prevIsLiked;
            likeCountRef.current = prevLikeCount;
            setIsLiked(prevIsLiked);
            setLikeCount(prevLikeCount);
            toast(getGraphQLErrorMsg(error.message));
        } finally {
            pendingRef.current = false;
        }
    }, [navigate, product, productId, unLikeProduct, likeProduct]);

    return (
        <ThrottledButton
            type="button"
            className="ProductLikeButton"
            onClick={handleLikePress}
            aria-label={isLiked ? "찜 해제" : "찜하기"}
        >
            <img
                className={`ProductLikeButton-icon ${isAnimating ? "ProductLikeButton-icon--animating" : ""}`}
                src={isLiked ? HeartFilledIcon : HeartOutlineIcon}
                alt=""
                aria-hidden="true"
                onAnimationEnd={handleAnimationEnd}
            />
            <span className="ProductLikeButton-count">{likeCount}</span>
        </ThrottledButton>
    );
}

LikeButton.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        is_liked: PropTypes.bool,
        like_count: PropTypes.number,
    }).isRequired,
};
